import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { authAPI, User, RegisterData, LoginData } from '../services/auth';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextType extends AuthState {
  login: (data: LoginData) => Promise<void>;
  register: (data: RegisterData) => Promise<{ emailSent?: boolean }>;
  loginWithGoogle: (idToken: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
  });

  // Check for existing session on mount
  useEffect(() => {
    const initAuth = async () => {
      if (authAPI.isAuthenticated()) {
        try {
          const user = await authAPI.getMe();
          setState({
            user,
            isLoading: false,
            isAuthenticated: true,
          });
        } catch {
          // Token might be expired, try to refresh
          try {
            await authAPI.refreshToken();
            const user = await authAPI.getMe();
            setState({
              user,
              isLoading: false,
              isAuthenticated: true,
            });
          } catch {
            // Refresh failed, clear auth
            authAPI.setAccessToken(null);
            localStorage.removeItem('refreshToken');
            setState({
              user: null,
              isLoading: false,
              isAuthenticated: false,
            });
          }
        }
      } else {
        setState({
          user: null,
          isLoading: false,
          isAuthenticated: false,
        });
      }
    };

    initAuth();
  }, []);

  // Token refresh interval
  useEffect(() => {
    if (!state.isAuthenticated) return;

    // Refresh token every 10 minutes
    const interval = setInterval(async () => {
      try {
        await authAPI.refreshToken();
      } catch {
        // Refresh failed, logout
        setState({
          user: null,
          isLoading: false,
          isAuthenticated: false,
        });
      }
    }, 10 * 60 * 1000);

    return () => clearInterval(interval);
  }, [state.isAuthenticated]);

  const login = useCallback(async (data: LoginData) => {
    const response = await authAPI.login(data);
    setState({
      user: response.user,
      isLoading: false,
      isAuthenticated: true,
    });
  }, []);

  const register = useCallback(async (data: RegisterData) => {
    const response = await authAPI.register(data);
    setState({
      user: response.user,
      isLoading: false,
      isAuthenticated: true,
    });
    return { emailSent: response.emailSent };
  }, []);

  const loginWithGoogle = useCallback(async (idToken: string) => {
    const response = await authAPI.loginWithGoogle(idToken);
    setState({
      user: response.user,
      isLoading: false,
      isAuthenticated: true,
    });
  }, []);

  const logout = useCallback(async () => {
    await authAPI.logout();
    setState({
      user: null,
      isLoading: false,
      isAuthenticated: false,
    });
  }, []);

  const refreshUser = useCallback(async () => {
    if (!authAPI.isAuthenticated()) return;

    try {
      const user = await authAPI.getMe();
      setState((prev) => ({
        ...prev,
        user,
      }));
    } catch {
      // Ignore errors
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        register,
        loginWithGoogle,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
